import React, { useEffect, useState } from 'react';
import { BarChart3, Eye, ShoppingCart, CreditCard, Users, TrendingUp } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#1a1a1a', '#4a4a4a', '#7a7a7a', '#a0a0a0', '#c0c0c0', '#d4d4d4', '#e5e5e5', '#f0f0f0'];

export default function AnalyticsTab({ getAuthHeaders }) {
  const [period, setPeriod] = useState('30');
  const [overview, setOverview] = useState(null);
  const [topArtworks, setTopArtworks] = useState([]);
  const [referrers, setReferrers] = useState([]);
  const [viewsOverTime, setViewsOverTime] = useState([]);
  const [topPages, setTopPages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAllAnalytics();
  }, [period]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAllAnalytics = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const [overviewRes, artworksRes, referrersRes, viewsRes, pagesRes] = await Promise.all([
        axios.get(`${API}/analytics/overview?days=${period}`, headers),
        axios.get(`${API}/analytics/top-artworks?days=${period}`, headers),
        axios.get(`${API}/analytics/referrers?days=${period}`, headers),
        axios.get(`${API}/analytics/views-over-time?days=${period}`, headers),
        axios.get(`${API}/analytics/pages?days=${period}`, headers),
      ]);
      setOverview(overviewRes.data);
      setTopArtworks(artworksRes.data);
      setReferrers(referrersRes.data);
      setViewsOverTime(viewsRes.data);
      setTopPages(pagesRes.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatPageName = (path) => {
    if (path === '/') return 'Homepage';
    if (path === '/gallery') return 'Gallery';
    if (path === '/about') return 'About';
    if (path === '/contact') return 'Contact';
    if (path === '/cart') return 'Cart';
    if (path === '/checkout') return 'Checkout';
    if (path === '/nsfw') return 'NSFW Collection';
    if (path.startsWith('/artwork/')) return 'Artwork Detail';
    if (path.startsWith('/nsfw/')) return 'NSFW Series';
    return path;
  };

  if (loading && !overview) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-foreground border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="analytics-tab">
      {/* Period selector */}
      <div className="flex items-center justify-between">
        <h2 className="text-xs uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-2">
          <BarChart3 className="h-4 w-4" />
          Site Analytics
        </h2>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-[180px]" data-testid="analytics-period-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="1">Today</SelectItem>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
            <SelectItem value="365">All time</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Overview cards */}
      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="analytics-overview">
          <StatCard icon={Eye} label="Page Views" value={overview.total_views} />
          <StatCard icon={Users} label="Unique Visitors" value={overview.unique_visitors} />
          <StatCard icon={Eye} label="Artwork Views" value={overview.artwork_views} />
          <StatCard icon={ShoppingCart} label="Cart Adds" value={overview.cart_adds} />
          <StatCard icon={CreditCard} label="Purchases" value={overview.purchases} />
          <StatCard icon={TrendingUp} label="Conversion" value={`${overview.conversion_rate}%`} />
        </div>
      )}

      {/* Views over time chart */}
      {viewsOverTime.length > 0 && (
        <div className="border border-border rounded-lg p-6" data-testid="analytics-views-chart">
          <h3 className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-6">Views Over Time</h3>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={viewsOverTime}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
              <XAxis
                dataKey="date"
                tickFormatter={formatDate}
                tick={{ fontSize: 11, fill: '#888' }}
                interval="preserveStartEnd"
              />
              <YAxis tick={{ fontSize: 11, fill: '#888' }} allowDecimals={false} />
              <Tooltip
                labelFormatter={formatDate}
                contentStyle={{ border: '1px solid #e5e5e5', borderRadius: '8px', fontSize: '12px' }}
              />
              <Line type="monotone" dataKey="page_views" stroke="#1a1a1a" strokeWidth={2} name="Page Views" dot={false} />
              <Line type="monotone" dataKey="artwork_views" stroke="#888" strokeWidth={2} name="Artwork Views" dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top artworks */}
        <div className="border border-border rounded-lg p-6" data-testid="analytics-top-artworks">
          <h3 className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-6">Most Viewed Artworks</h3>
          {topArtworks.length > 0 ? (
            <div className="space-y-3">
              {topArtworks.map((item, idx) => (
                <div key={item.artwork_id} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-muted-foreground w-5">{idx + 1}.</span>
                    <span className="text-sm truncate max-w-[200px]">{item.title || 'Unknown'}</span>
                  </div>
                  <span className="text-sm font-medium">{item.views} views</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No artwork views yet</p>
          )}
        </div>

        {/* Referrers / Traffic sources */}
        <div className="border border-border rounded-lg p-6" data-testid="analytics-referrers">
          <h3 className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-6">Traffic Sources</h3>
          {referrers.length > 0 ? (
            <div className="space-y-6">
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={referrers.slice(0, 8)}
                    dataKey="count"
                    nameKey="source"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    strokeWidth={1}
                  >
                    {referrers.slice(0, 8).map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ border: '1px solid #e5e5e5', borderRadius: '8px', fontSize: '12px' }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-2">
                {referrers.map((item, idx) => (
                  <div key={item.source} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[idx % COLORS.length] }} />
                      <span className="truncate max-w-[180px]">{item.source}</span>
                    </div>
                    <span className="font-medium">{item.count}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No traffic data yet</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top pages */}
        <div className="border border-border rounded-lg p-6" data-testid="analytics-top-pages">
          <h3 className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-6">Most Visited Pages</h3>
          {topPages.length > 0 ? (
            <div className="space-y-3">
              {topPages.map((item) => (
                <div key={item.page} className="flex items-center justify-between">
                  <span className="text-sm">{formatPageName(item.page)}</span>
                  <span className="text-sm font-medium">{item.views} views</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No page views yet</p>
          )}
        </div>

        {/* Cart to purchase funnel */}
        {overview && (overview.cart_adds > 0 || overview.purchases > 0) && (
          <div className="border border-border rounded-lg p-6" data-testid="analytics-funnel">
            <h3 className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-6">Purchase Funnel</h3>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={[
                { name: 'Artwork Views', value: overview.artwork_views },
                { name: 'Cart Adds', value: overview.cart_adds },
                { name: 'Purchases', value: overview.purchases },
              ]}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#888' }} />
                <YAxis tick={{ fontSize: 11, fill: '#888' }} allowDecimals={false} />
                <Tooltip contentStyle={{ border: '1px solid #e5e5e5', borderRadius: '8px', fontSize: '12px' }} />
                <Bar dataKey="value" fill="#1a1a1a" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="border border-border rounded-lg p-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="h-4 w-4 text-muted-foreground" />
        <span className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{label}</span>
      </div>
      <p className="text-2xl font-medium" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>{value}</p>
    </div>
  );
}
